import { useState } from 'react';
import type { ProductionRequirementView, ResourceType, SourcingDecisionBody } from '@scoutaround/script-breakdown-shared';

import { api } from '../../lib/api.js';
import { useMembership, useRefusal } from '../../lib/permissions.js';
import { POINTED_AT_STYLE, useScrollIntoViewWhen } from '../../lib/useScrollIntoViewWhen.js';
import { Disclosure } from '../ui/Disclosure.js';
import { EmptyState } from '../ui/EmptyState.js';
import { MoreMenu, type MenuItem } from '../ui/MoreMenu.js';
import { StatusPill } from '../ui/StatusPill.js';
import { usePlan, usePlanWrite, writeError } from './context.js';
import { specFields } from './format.js';
import {
  APPLICABILITY_LABELS,
  NECESSITY_LABELS,
  REVIEW_STATE,
  RESOURCE_LABELS,
  RESOURCE_TYPES,
  SOURCING_LABELS,
} from './labels.js';
import { EvidenceQuotes, IncompleteBanner, PlanLoadState, sceneRequirements, shortDate } from './PlanParts.js';

/**
 * WHAT WOULD HAVE TO BE FOUND FOR THIS SCENE — the Resources lens.
 *
 * The scene's requirements that name a resource, grouped by kind, with their
 * specification (only the fields that are known) and where finding it stands.
 * ScoutAround can search one kind today, locations; a match there is a
 * CANDIDATE, never proof it is available. Every other kind is found outside
 * ScoutAround, and the screen says so rather than offering a search it cannot
 * run. A person records how it is going: being found elsewhere, or confirmed
 * (with a reference or a note saying how).
 */
export function ResourcesView({ sceneId }: { sceneId: string }) {
  const { plan } = usePlan();

  if (plan === null) {
    return (
      <section className="ui-panel pp-panel" data-testid="production-resources">
        <div className="ui-panel-body">
          <PlanLoadState testId="production-resources" />
        </div>
      </section>
    );
  }

  const rows = sceneRequirements(plan, sceneId).filter(
    (r): r is ProductionRequirementView & { resourceType: ResourceType } => r.resourceType !== null,
  );

  return (
    <section className="ui-panel pp-panel" data-testid="production-resources" aria-labelledby="production-resources-title">
      <div className="ui-panel-head">
        <h3 className="ui-panel-title" id="production-resources-title">
          What has to be found
        </h3>
      </div>
      <div className="ui-panel-body pp-stack">
        <IncompleteBanner plan={plan} />
        {rows.length === 0 ? (
          <EmptyState title="Nothing to find for this scene" testId="production-resources-empty">
            No requirement on this scene names a resource.
          </EmptyState>
        ) : (
          RESOURCE_TYPES.filter((type) => rows.some((r) => r.resourceType === type)).map((type) => {
            const group = rows.filter((r) => r.resourceType === type);

            return (
              <div key={type} className="pp-group" data-testid={`production-resource-group-${type}`}>
                <h4 className="pp-group-title">{`${RESOURCE_LABELS[type]} · ${String(group.length)}`}</h4>
                <ul className="pp-list">
                  {group.map((r) => (
                    <ResourceRow key={r.id} requirement={r} sceneId={sceneId} />
                  ))}
                </ul>
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}

type SourcingInput = Omit<SourcingDecisionBody, 'rowVersion'>;

function ResourceRow({ requirement, sceneId }: { requirement: ProductionRequirementView; sceneId: string }) {
  const { projectId, focusRequirementId, onOpenLocationRequirement, showEvidence } = usePlan();
  const refusal = useRefusal('SOURCING_ACTIVATE');
  const me = useMembership()?.userId ?? null;
  const [confirming, setConfirming] = useState(false);
  const id = requirement.id;
  const pointed = focusRequirementId === id;
  const ref = useScrollIntoViewWhen<HTMLLIElement>(pointed);
  const write = usePlanWrite((input: SourcingInput) =>
    api.setPlanSourcing(projectId, id, { ...input, rowVersion: requirement.rowVersion }),
  );

  const sourcing = requirement.sourcing;
  const locationLink = sourcing?.locationRequirementId ?? null;
  const fields = requirement.resourceSpec === null ? [] : specFields(requirement.resourceSpec);
  const status = REVIEW_STATE[requirement.reviewStatus];
  const inCatalogue = sourcing?.capability === 'SUPPORTED' || (sourcing === null && requirement.resourceType === 'LOCATION');
  const blocked = write.isPending || refusal !== null;
  /**
   * WHERE THIS RESOURCE COMES FROM IN THE SCENE, only beside the reader. The Resources lens is about
   * finding a thing, not reading the screenplay, so it showed no quotes; with the reader on screen a
   * quote is the way to put the line in view, and nothing changes where there is no reader.
   */
  const here = showEvidence === undefined ? [] : (requirement.scenes.find((s) => s.sceneId === sceneId)?.evidence ?? []);

  const items: MenuItem[] = [
    ...(sourcing?.status === 'EXTERNAL_SOURCING'
      ? []
      : [
          {
            id: 'external',
            label: 'Being found outside ScoutAround',
            testId: `production-resource-external-${id}`,
            onSelect: () => write.mutate({ status: 'EXTERNAL_SOURCING' }),
          },
        ]),
    {
      id: 'confirm',
      label: 'Record it as confirmed',
      testId: `production-resource-confirm-${id}`,
      onSelect: () => {
        setConfirming(true);
      },
    },
    ...(sourcing === null || sourcing.status === 'NOT_SEARCHED'
      ? []
      : [
          {
            id: 'reset',
            label: 'Reset to not searched',
            testId: `production-resource-reset-${id}`,
            onSelect: () => write.mutate({ status: 'NOT_SEARCHED' }),
          },
        ]),
  ].map((item) => (blocked ? { ...item, disabled: true, hint: refusal ?? 'Saving…' } : item));

  return (
    <li
      ref={ref}
      className="pp-req"
      data-testid={`production-resource-${id}`}
      data-applicability={requirement.effectiveApplicability}
      data-pinned={requirement.requirementType === 'SAFETY_REVIEW' ? '1' : undefined}
      style={pointed ? POINTED_AT_STYLE : undefined}
    >
      <div className="pp-req-head">
        <span className="pp-req-name">{requirement.name}</span>
        <StatusPill state={status.state} label={status.label} size="sm" />
        <span className="ui-toolbar-end">
          <MoreMenu items={items} testId={`production-resource-more-${id}`} label={`How finding ${requirement.name} is going`} />
        </span>
      </div>

      <p className="pp-req-line">
        <span className="pp-applicability" data-testid={`production-resource-applicability-${id}`}>
          {APPLICABILITY_LABELS[requirement.effectiveApplicability]}
        </span>
        <span>
          {` · ${NECESSITY_LABELS[requirement.necessity]}`}
          {requirement.condition === null ? '' : `: ${requirement.condition.text}`}
        </span>
      </p>

      {fields.length === 0 ? (
        <p className="mm pp-note">Nothing about it is specified yet.</p>
      ) : (
        <dl className="pp-facts" data-testid={`production-resource-spec-${id}`}>
          {fields.map((f) => (
            <div key={f.key}>
              <dt>{f.label}</dt>
              <dd>{f.value}</dd>
            </div>
          ))}
        </dl>
      )}

      <div className="pp-sourcing" data-testid={`production-resource-sourcing-${id}`} data-status={sourcing?.status ?? 'NONE'}>
        <SourcingState requirement={requirement} me={me} />
        {inCatalogue ? (
          locationLink !== null && onOpenLocationRequirement !== undefined ? (
            <button
              type="button"
              className="ctl"
              data-testid={`production-resource-open-location-${id}`}
              onClick={() => {
                onOpenLocationRequirement(locationLink);
              }}
            >
              Open in Locations
            </button>
          ) : null
        ) : (
          <p className="mm pp-note" data-testid={`production-resource-outside-${id}`}>
            Not in the ScoutAround catalogue: it has to be found outside ScoutAround.
          </p>
        )}
      </div>

      {here.length === 0 ? null : (
        <Disclosure
          summary="Evidence"
          meta={`${String(here.length)} quote${here.length === 1 ? '' : 's'}`}
          testId={`production-resource-evidence-${id}`}
        >
          <EvidenceQuotes spans={here} sceneId={sceneId} testId={`production-resource-quotes-${id}`} />
        </Disclosure>
      )}

      {confirming ? (
        <ConfirmForm
          id={id}
          busy={write.isPending}
          onCancel={() => {
            setConfirming(false);
          }}
          onSave={(input) => {
            write.mutate(
              { status: 'CONFIRMED', ...input },
              {
                onSuccess: () => {
                  setConfirming(false);
                },
              },
            );
          }}
        />
      ) : null}

      {write.error === null ? null : (
        <p className="pp-error" role="alert" data-testid={`production-resource-error-${id}`}>
          {writeError(write.error)}
        </p>
      )}
    </li>
  );
}

function SourcingState({ requirement, me }: { requirement: ProductionRequirementView; me: number | null }) {
  const sourcing = requirement.sourcing;
  if (sourcing === null) return <p className="pp-sourcing-state">{SOURCING_LABELS.NOT_SEARCHED}</p>;

  if (sourcing.status === 'MATCH_CANDIDATES_FOUND') {
    return (
      <p className="pp-sourcing-state">
        <strong>
          {SOURCING_LABELS.MATCH_CANDIDATES_FOUND}
          {sourcing.candidateCount === null ? '' : ` (${String(sourcing.candidateCount)})`}
        </strong>
        <span className="mm"> — a candidate, not proof it is available.</span>
      </p>
    );
  }

  if (sourcing.status === 'CONFIRMED') {
    const when = shortDate(sourcing.confirmedAt);
    const who =
      sourcing.confirmedByUserId === null
        ? null
        : me !== null && sourcing.confirmedByUserId === String(me)
          ? 'you'
          : 'a teammate';

    return (
      <div className="pp-sourcing-state">
        <strong>{SOURCING_LABELS.CONFIRMED}</strong>
        <span className="mm">
          {who === null ? '' : ` by ${who}`}
          {when === null ? '' : ` on ${when}`}
        </span>
        {sourcing.reference === null ? null : <p className="pp-note">{`Reference: ${sourcing.reference}`}</p>}
        {sourcing.note === null ? null : <p className="pp-note">{sourcing.note}</p>}
      </div>
    );
  }

  return (
    <p className="pp-sourcing-state">
      <strong>{SOURCING_LABELS[sourcing.status]}</strong>
      {sourcing.note === null ? null : <span className="mm">{` — ${sourcing.note}`}</span>}
    </p>
  );
}

/** Confirmed needs a reference or a note saying how it was confirmed — the server refuses it otherwise. */
function ConfirmForm({
  id,
  busy,
  onCancel,
  onSave,
}: {
  id: string;
  busy: boolean;
  onCancel: () => void;
  onSave: (input: { reference?: string; note?: string }) => void;
}) {
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const valid = reference.trim() !== '' || note.trim() !== '';

  return (
    <form
      className="pp-form"
      data-testid={`production-resource-confirm-form-${id}`}
      onSubmit={(event) => {
        event.preventDefault();
        if (!valid) return;
        onSave({
          ...(reference.trim() === '' ? {} : { reference: reference.trim() }),
          ...(note.trim() === '' ? {} : { note: note.trim() }),
        });
      }}
    >
      <label className="pp-field">
        <span>Reference</span>
        <input
          value={reference}
          maxLength={300}
          placeholder="A booking, an order or a contract number"
          data-testid={`production-resource-reference-${id}`}
          onChange={(e) => {
            setReference(e.target.value);
          }}
        />
      </label>
      <label className="pp-field pp-field-wide">
        <span>Note</span>
        <textarea
          value={note}
          maxLength={500}
          rows={2}
          placeholder="How it was confirmed"
          data-testid={`production-resource-note-${id}`}
          onChange={(e) => {
            setNote(e.target.value);
          }}
        />
      </label>
      {valid ? null : <p className="mm pp-note">Give a reference or a note saying how it was confirmed.</p>}
      <div className="ui-toolbar">
        <button type="submit" className="ctl" disabled={!valid || busy} data-testid={`production-resource-confirm-save-${id}`}>
          {busy ? 'Saving…' : 'Record as confirmed'}
        </button>
        <button type="button" className="ctl" onClick={onCancel} data-testid={`production-resource-confirm-cancel-${id}`}>
          Cancel
        </button>
      </div>
    </form>
  );
}
